import { DynamicController } from "./DynamicController";
import { Field } from "../../Pages/Measure/Dependencies/Field";
import { ColorHelper } from "../ColorHelper";

type BaseControlListener = (control: BaseControl, iteration: number) => void;

export abstract class BaseControl {
    protected measureId: number;
    public type: string;
    public fieldName: string;
    public fieldId: number;
    public categoryId: number;
    public parentcategory?: number;
    public templateId: number;
    public templateBlockId: number;
    public isDisabled: boolean;
    protected isHidden: boolean;
    protected isRequired: boolean;
    protected inMatrix: boolean;
    private changeListener: Array<BaseControlListener> = [];
    public isDataFromSubmeasures: boolean;
    public isDataFromParentId: boolean;
    public internalName: string;

    protected allowDependencyChange: boolean = false;
    protected changeIsMandatory: boolean = false;

    public get isIteration(): boolean {
        return ("createIterationControl" in this);
    }

    public get HasChangedElement() {
        // A getter is needed, because displaying the field in a matrix would set the element to undefined.
        if (this.allowDependencyChange) {
            // allowDependencyChange is determined in the constructor, before the field might be put in a matrix.
            if (!this.hasChangedElement)
                if (this.categoryId != 0) {
                    this.hasChangedElement = this.element.parentElement.querySelector(`#catField_${this.categoryId}_changedByUser`) as HTMLInputElement;
                } else {
                    this.hasChangedElement = this.element.parentElement.querySelector(`#dynField_${this.fieldId}_changedByUser`) as HTMLInputElement;
                }
        }
        return this.hasChangedElement;
    }

    private hasChangedElement: HTMLInputElement;

    public get changedByUser(): boolean {
        return this.HasChangedElement?.value.toLowerCase() === "true";
    }

    public abstract isEmpty(): boolean;
    public abstract getValue(iteration: number): any;

    public updateValueFromServer = () => {
        // override in concrete classes if needed
    }

    public dependencyFields: Array<Field>;
    public validationDependencyFields: Array<Field>;

    constructor(protected element: HTMLElement, protected controller: DynamicController, measureId?: number) {
        if (measureId) {
            this.measureId = measureId;
        } else {
            this.measureId = Number((document.getElementById("Model_Id") as HTMLInputElement).value);
        }
        this.type = this.element.getAttribute("data-type");
        this.fieldName = this.element.getAttribute("data-field");
        this.fieldId = parseInt(this.element.getAttribute("data-field-id"));
        this.categoryId = this.element.getAttribute("data-category-id") ? parseInt(this.element.getAttribute("data-category-id")) : 0;
        this.templateId = parseInt(this.element.getAttribute("data-template-id"));
        this.templateBlockId = parseInt(this.element.getAttribute("data-template-block-id"));
        this.isDisabled = this.element.getAttribute("disabled") == "disabled";
        this.isRequired = this.element.getAttribute("data-required") == "true";
        this.inMatrix = this.element.getAttribute("data-matrix") == "true";
        this.element.addEventListener("focus", this.onFocus);
        this.changeIsMandatory = this.element.getAttribute("data-change-is-mandatory") == "true";
        this.isDataFromSubmeasures = this.element.getAttribute("data-from-submeasures") == "true";
        this.isDataFromParentId = this.element.getAttribute("data-from-parent-id") == "true";
        this.internalName = this.element.getAttribute("data-internal-name");

        this.isHidden = (this.getContainer()?.classList.contains("dyn-hidden") || element.classList.contains("dyn-hidden")) ?? false;

        if ((this.element.parentElement?.querySelector(`#dynField_${this.fieldId}_changedByUser`) as HTMLInputElement)
            || (this.element.parentElement?.querySelector(`#catField_${this.categoryId}_changedByUser`) as HTMLInputElement)) {
            this.allowDependencyChange = true;
        }

    }

    public addOnChangeListener(listener: BaseControlListener) {
        this.changeListener.push(listener);
    }

    public hasOnChangeListener(listener: BaseControlListener) {
        if (this.changeListener.indexOf(listener) > -1) return true;
        return false;
    }

    public onChange(iteration: number = 0) {
        for (let listener of this.changeListener) {
            listener(this, iteration);
        }
    }

    protected setModifiedByUser = () => {
        if (this.HasChangedElement) {
            this.HasChangedElement.value = "true";
        }
    }

    public onFocus = () => {
        this.getContainer().classList.remove("error");
        this.setHighlightedStatus(this.templateBlockId);
    }

    public setHighlightedStatus(blockId: number) {
        document.querySelectorAll('[id^="leftStatusBlock_"]').forEach(function (item) {
            item.classList.remove("active-st");
        });

        document.getElementById("leftStatusBlock_" + blockId.toString())?.classList.add("active-st");
    }

    public beforeSubmit() {
    }

    public afterFailedSubmit = undefined;


    public validate(): boolean {
        if (this.isRequired && !this.isHidden && !this.isDisabled && this.isEmpty()) {
            if (this.inMatrix)
                this.getMatrixCellContainer().classList.add("dyn-required-cell");
            else
                this.getContainer().classList.add("error");
            return false;
        }
        return true;
    }

    public validateChangeIsMandatory(): boolean {
        if (this.allowDependencyChange && this.changeIsMandatory && !this.changedByUser && !this.isHidden) {
            this.getContainer().classList.add("error");
            return false;
        }
        return true;
    }


    public scollIntoView() {
        let scrollContainer = document.querySelector(".pag-content-scroll");
        if (scrollContainer) {

            var scrollTimeout;
            let scrollListener = (e) => {
                clearTimeout(scrollTimeout);
                scrollTimeout = setTimeout(() => {
                    console.log('Scroll ended');

                    scrollContainer.scrollBy(0, -(40 / window.devicePixelRatio));

                    scrollContainer.removeEventListener("scroll", scrollListener);

                }, 100);
            }

            scrollContainer.addEventListener('scroll', scrollListener);
            this.element.scrollIntoView({ behavior: "smooth", block: "start" });
            //scrollContainer.removeEventListener("scroll", scrollListener);
        } else {
            this.element.scrollIntoView({ behavior: "smooth", block: "start" });
        }
    }

    protected getContainer(): HTMLElement {
        let result = this.element;
        while (result && !result.classList.contains("dyn-control-frame")) {
            result = result.parentElement;
        }
        return result;
    }

    protected getMatrixCellContainer(): HTMLElement {
        let result = this.element.parentElement;
        while (result && !result.classList.contains("matrix-cell")) {
            result = result.parentElement;
        }
        return result;
    }

    setVisibility(isVisible: boolean) {
        let element = this.element;

        // Some Elements have a preElement - the label is not part of the element itself.
        let preElement: HTMLElement;
        let actionsElement: HTMLElement;
        let finishedElement: HTMLElement;
        let finishedLabelElement: HTMLElement;

        while (element && element.parentElement
            && !((element.classList.contains("dyn-control-frame") && !element.parentElement.classList.contains("category-rows"))
                || element.classList.contains("matrix-cell")
                || element.classList.contains("category-rows")
                //|| element.classList.contains("tabbed-matrix")
            )) {
            element = element.parentElement;
            if (!actionsElement) {
                actionsElement = element.querySelector(".action-container");
                finishedElement = element.querySelector(".dyn-finished-container");

                if (actionsElement) {
                    let controlLeft = element.parentElement?.parentElement?.querySelector(".dyn-control-left") as HTMLElement;
                    if (controlLeft) {
                        finishedLabelElement = controlLeft.querySelector(".dyn-label");
                    }
                }
            }

        }

        preElement = this.element.previousSibling as HTMLElement;

        if (isVisible) {
            if (actionsElement && finishedElement && finishedLabelElement) {
                // if "actionsElement" was found, only the "finishedElement" and "finishedLabelElement" were hidden
                finishedElement.classList.remove("d-none");
                finishedLabelElement.classList.remove("d-none");
            } else {
                element.classList.remove("dyn-hidden");
            }
            if (preElement && preElement.classList.contains("dyn-label-row")) {
                preElement.classList.remove("dyn-hidden");
            }
        }
        else {
            if (actionsElement && finishedElement && finishedLabelElement) {
                // if "actionsElement" was found, hide only the "finishedElement" and "finishedLabelElement"
                finishedElement.classList.add("d-none");
                finishedLabelElement.classList.add("d-none");
            } else {
                element.classList.add("dyn-hidden");
            }
            if (preElement && preElement.classList.contains("dyn-label-row")) {
                preElement.classList.add("dyn-hidden");
            }

        }

        this.isHidden = !isVisible;
    }

    public getTextColor(color: string): string {
        return ColorHelper.getContrastingTextColor(color);
    }
}